import { AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * What a vendor actually needs to know about their own price.
 *
 * The platform fee is added on top of what the vendor enters: they list at ₹500
 * and the shopper is charged ₹550. Showing only the vendor's own number is how a
 * commission comes as a surprise later — either because they never realised the
 * customer pays more, or because they assumed it was coming out of their margin.
 *
 * So all three figures appear together, per variant, with the vendor's take-home
 * emphasised because it is the one they care about most.
 */
export type VariantPricing = {
  variant_id: string;
  label?: string;
  vendor_price_inr: string;
  mrp_inr: string | null;
  platform_fee_inr: string;
  customer_price_inr: string;
  commission_rate_pct: number | null;
  commission_mode: 'RATE' | 'FLAT';
  /** Which level set the fee — a vendor asking "why this number" needs it. */
  commission_source: 'VARIANT' | 'PRODUCT' | 'CATEGORY' | 'NONE';
  /** The fee pushed the shopper's price above the vendor's own printed MRP. */
  exceeds_mrp: boolean;
};

const rupees = (v: string) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** How the fee was set, in words a vendor can act on. */
function fee_note(p: VariantPricing): string {
  if (p.commission_source === 'NONE') return 'No platform fee set yet';
  const amount =
    p.commission_mode === 'FLAT'
      ? `${rupees(p.platform_fee_inr)} flat`
      : `${p.commission_rate_pct ?? 0}%`;
  const where =
    p.commission_source === 'VARIANT'
      ? 'set for this size'
      : p.commission_source === 'PRODUCT'
        ? 'set for this product'
        : 'standard for this category';
  return `${amount}, ${where}`;
}

export function ProductPricingPanel({
  pricing,
  className,
}: {
  pricing: VariantPricing[] | undefined;
  className?: string;
}) {
  if (!pricing || pricing.length === 0) return null;

  return (
    <div
      className={cn('rounded-card border-2 border-brand-outline-variant/15 bg-secondary/20 p-3', className)}
      data-testid="pricing-panel"
    >
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        What customers pay
      </div>

      <div className="space-y-2">
        {pricing.map((p) => (
          <div key={p.variant_id} className="text-sm" data-testid={`pricing-${p.variant_id}`}>
            {p.label && <p className="mb-0.5 text-xs font-medium text-muted-foreground">{p.label}</p>}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 tabular-nums">
              <span>
                <span className="text-xs text-muted-foreground">You get </span>
                <span className="font-semibold" data-testid={`vendor-price-${p.variant_id}`}>
                  {rupees(p.vendor_price_inr)}
                </span>
              </span>
              {p.mrp_inr && (
                <span className="text-xs text-muted-foreground">
                  MRP <span className="line-through">{rupees(p.mrp_inr)}</span>
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                + fee {rupees(p.platform_fee_inr)}
              </span>
              <span className="ml-auto">
                <span className="text-xs text-muted-foreground">Customer pays </span>
                <span
                  className="font-heading text-base font-bold text-primary"
                  data-testid={`customer-price-${p.variant_id}`}
                >
                  {rupees(p.customer_price_inr)}
                </span>
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{fee_note(p)}</p>

            {p.exceeds_mrp && (
              /*
               * A listing priced above its own printed MRP reads as an error to a
               * shopper, so the vendor is told before they wonder why it stopped
               * selling. Their own price is not the problem — the fee on top is.
               */
              <p
                className="mt-1 flex items-start gap-1 text-[11px] font-medium text-destructive"
                data-testid={`exceeds-mrp-${p.variant_id}`}
              >
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                With the fee, customers pay more than the MRP you set. Consider a lower price or a higher MRP.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
